import { useQuery } from '@apollo/client';
import { READ_CARD } from '@queries/readCard';
import { useFocusEffect } from '@react-navigation/native';
import { Spinner } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import WebView, { WebViewNavigation } from 'react-native-webview';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { Card, Query } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';
import {
  defineNetsUrlType,
  getNetsCardId,
  getParameterByName,
} from 'utils/urlUtils';
import { useUiStore } from 'stores/uiStore';

type PaymentMethodFormProps = {
  afterFailure?: () => void;
  afterSuccess?: () => void;
  isTesting?: boolean;
  isModal?: boolean;
};

const contentStyle = {
  paddingBottom: 40,
};

/** Bottom padding so the WebView is shorter and the "Hyväksy" button can scroll into view. */
const BOTTOM_PADDING_FOR_BUTTON = 80;

const CreditCardFormContainer = styled.View<{ $bottomPadding?: number }>`
  height: 100%;
  padding-bottom: ${(p) => p.$bottomPadding ?? 0}px;
`;

const CreditCardForm = ({
  afterSuccess,
  afterFailure,
  isTesting = false,
  isModal = false,
}: PaymentMethodFormProps) => {
  const insets = useSafeAreaInsets();
  const {
    loading: customerLoading,
    refetchCustomer: fetchCustomerAfterSuccess,
  } = useContext(AccountContext);
  const creditCardUrl = usePaymentStore((s) => s.creditCardCreationTerminalUrl);
  const containerBottomPadding = Math.max(
    insets.bottom,
    BOTTOM_PADDING_FOR_BUTTON
  );
  const [cardId, setCardId] = useState<string | undefined>(undefined);
  const [isLoadingFrame, setIsLoadingFrame] = useState<boolean>(true);
  const [cardVerificationInProgress, setCardVerificationInProgress] =
    useState<boolean>(false);

  const onAddCardSuccess = useCallback(() => {
    showToast('success.verifyPaymentCard', 'success');
    afterSuccess && afterSuccess();
  }, [afterSuccess]);

  const {
    loading: readCardLoading,
    startPolling: startPollingCard,
    stopPolling: stopPollingCard,
  } = useQuery<Query>(READ_CARD, {
    variables: {
      cardId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !cardId,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const { expMonth, expYear, isDefault, lastNums, type } =
        data.readCard as Card;
      if (data.readCard?.status === 'Active') {
        usePaymentStore.getState().setSelectedPaymentCard({
          cardId: cardId || '',
          title: type || '',
          subtitle: lastNums ? ` **** ${lastNums}` : '',
          category: '',
          status: '',
          isDefault: isDefault === true,
          description: '',
          newCard: true,
          expMonth: expMonth || '',
          expYear: expYear || '',
        });
        onAddCardSuccess();
        if (!afterSuccess) {
          useUiStore.getState().setSheetModal('paymentCard');
        }
      }
    },
    onError: () => {
      usePaymentStore.getState().resetSelectedPaymentCard();
      showToast('error.verifyPaymentCard', 'error');
      if (afterFailure) {
        afterFailure();
      }
      useUiStore.getState().setSheetModal('');
    },
  });

  const handleUrlChange = useCallback(
    async (url: string) => {
      const state = defineNetsUrlType(url);
      switch (state) {
        case 'cancel':
          usePaymentStore
            .getState()
            .setCreditCardCreationTerminalUrl(undefined);
          afterFailure && afterFailure();
          useUiStore.getState().setSheetModal('');
          break;
        case 'success':
          // For NETS, verification happens asynchronous. Start polling the card to be update the status.
          const netsCardId = getNetsCardId(url);
          setCardId(netsCardId);
          usePaymentStore.getState().setSelectedPaymentCard({
            cardId: netsCardId || '',
            title: '',
            subtitle: '',
            category: '',
            status: 'Verifying',
            isDefault: false,
            description: '',
            newCard: true,
            expMonth: '',
            expYear: '',
          });
          setCardVerificationInProgress(true);
          startPollingCard(3000);
          break;
        case 'failure':
          showToast('error.verifyPaymentCard', 'error');
          const reason = getParameterByName('reason', url);
          Logger.error(
            `Verify Payment Card | ${
              reason !== '' ? 'Reason: ' + reason : 'Url: ' + url
            }`
          );
          usePaymentStore
            .getState()
            .setCreditCardCreationTerminalUrl(undefined);
          afterFailure && afterFailure();
          useUiStore.getState().setSheetModal('');
          break;
      }
    },
    [afterFailure, startPollingCard]
  );

  useEffect(() => {
    if (isTesting && creditCardUrl) {
      handleUrlChange(creditCardUrl || '');
    }
  }, [handleUrlChange, creditCardUrl, isTesting]);

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        // This function will run when the screen loses focus
        // Clean up any resources or subscriptions here
        fetchCustomerAfterSuccess();
        setCardVerificationInProgress(false);
        setCardId(undefined);
        stopPollingCard();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const onNavigationStateChange = async (event: WebViewNavigation) => {
    await handleUrlChange(event.url);
  };

  // If there's a type for target.contentWindow, could be used
  const onloadStateChange = async (event: any) => {
    setIsLoadingFrame(false);
    try {
      const url = event.target.contentWindow.location.href;
      await handleUrlChange(url);
    } catch (e) {
      return;
    }
  };

  if (customerLoading || readCardLoading || cardVerificationInProgress) {
    return (
      <StyledWrapper center testID="verify-credit-card">
        <Spinner />
      </StyledWrapper>
    );
  }

  if (creditCardUrl) {
    return (
      <CreditCardFormContainer $bottomPadding={containerBottomPadding}>
        {isLoadingFrame && (
          <StyledWrapper center>
            <Spinner />
          </StyledWrapper>
        )}
        {isWeb ? (
          <iframe
            id="add-credit-card-iframe"
            frameBorder="0"
            height={isModal ? 500 : '100%'}
            width="100%"
            src={creditCardUrl}
            onLoad={onloadStateChange}
          />
        ) : (
          <WebView
            testID="add-credit-card-webview"
            useWebKit
            width="100%"
            style={[{ flex: 1 }, contentStyle]}
            source={{ uri: creditCardUrl }}
            onNavigationStateChange={onNavigationStateChange}
            nestedScrollEnabled={true}
            injectedJavaScript={`
              document.getElementById('securityCode').addEventListener('keydown', function(event) {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.blur();
                  document.getElementById('okButton').click();
                }
              });
            `}
            onLoad={() => setIsLoadingFrame(false)}
          />
        )}
      </CreditCardFormContainer>
    );
  }
  return <></>;
};

export default CreditCardForm;
